<template>
  <div class="cu-timeline">
    <div v-if="!rows.length" class="cu-empty text-muted small">No channels configured for matches on this day yet.</div>

    <template v-else>
      <div class="cu-row" v-for="row in rows" :key="row.key">
        <div class="cu-label"><span v-if="row.warn" class="cu-dot-warn" title="A source on this row is not added to any event"></span>{{ row.label }}</div>
        <div class="cu-track">
          <div
              v-for="bar in row.bars"
              :key="bar.calMatchId + '-shadow'"
              class="cu-shadow"
              :class="markClass(bar, bar.shadowCutLeft, bar.shadowCutRight)"
              :style="{ left: bar.shadowLeft + '%', width: bar.shadowWidth + '%' }"
              @click="$emit('bar-click', bar.calMatchId)"
              @mouseenter="showTip($event, bar)"
              @mouseleave="hideTip"
          ></div>
          <div
              v-for="bar in row.bars"
              v-show="bar.width > 0"
              :key="bar.calMatchId"
              class="cu-bar"
              :class="markClass(bar, bar.cutLeft, bar.cutRight)"
              :style="{ left: bar.left + '%', width: bar.width + '%' }"
              tabindex="0"
              role="button"
              :aria-label="bar.tooltip"
              @click="$emit('bar-click', bar.calMatchId)"
              @keydown.enter="$emit('bar-click', bar.calMatchId)"
              @mouseenter="showTip($event, bar)"
              @mouseleave="hideTip"
              @focus="showTip($event, bar)"
              @blur="hideTip"
          >{{ bar.label }}</div>
        </div>
      </div>

      <div class="cu-axis">
        <span v-for="h in axisHours" :key="h">{{ h }}</span>
      </div>

      <p class="cu-caption text-muted small">
        The faded extension behind each bar is that source's own encoder up/down window
        (match start - lead minutes / match end + trail minutes), usually a little wider than the match itself.
        A flat edge means the bar continues past this day's view. A dashed bar is an inactive source, and an amber striped bar is a source not added to any event.
      </p>

      <div v-if="tip.visible" class="cu-tooltip" :style="{ left: tip.x + 'px', top: tip.y + 'px' }">
        <div v-for="(line, i) in tip.lines" :key="i" class="cu-tooltip-line">{{ line }}</div>
      </div>
    </template>
  </div>
</template>

<script>
// Presentational-only: geometry (left/width percentages within the day's 24h window, colors,
// cut/inactive flags) is computed by the parent (Daily.vue) and passed in as `rows`. This
// component just renders bars and reports clicks - it doesn't know about matches, channels,
// or dates at all, so it can be reused wherever the same row/bar shape makes sense.
//
// Bar colors come from the dataviz skill's validated 8-slot categorical palette (light mode;
// this app has no dark mode) - see docs/be-tasks/... discussion / dataviz skill palette.md.
// Slots are assigned by the parent in a fixed order (never re-cycled within a render), one per
// match. A 9th+ concurrent match on the same day falls back to a shared neutral "overflow"
// style rather than reusing an earlier match's color (see cu-overflow below) - matches stay
// identifiable primarily through their own direct label either way.
export default {
  name: 'ChannelUsageTimeline',
  props: {
    // [{ key, label, bars: [{ calMatchId, left, width, shadowLeft, shadowWidth, cutLeft,
    //   cutRight, shadowCutLeft, shadowCutRight, colorSlot (1-8 or null), inactive, label,
    //   tooltip }] }] - left/width/shadowLeft/shadowWidth are percentages of the 24h window.
    rows: {
      type: Array,
      default: () => []
    }
  },
  emits: ['bar-click'],
  data() {
    return {
      axisHours: ['00', '02', '04', '06', '08', '10', '12', '14', '16', '18', '20', '22'],
      tip: { visible: false, x: 0, y: 0, lines: [] }
    };
  },
  methods: {
    markClass(bar, cutLeft, cutRight) {
      return {
        [`cu-series-${bar.colorSlot}`]: !bar.inactive && !bar.noEvent && !!bar.colorSlot,
        'cu-overflow': !bar.inactive && !bar.noEvent && !bar.colorSlot,
        'cu-noevent': !bar.inactive && !!bar.noEvent,
        'cu-inactive': bar.inactive,
        'cu-cut-l': cutLeft,
        'cu-cut-r': cutRight
      };
    },
    showTip(evt, bar) {
      const targetRect = evt.currentTarget.getBoundingClientRect();
      const rootRect = this.$el.getBoundingClientRect();
      this.tip = {
        visible: true,
        x: targetRect.left - rootRect.left + targetRect.width / 2,
        y: targetRect.top - rootRect.top,
        lines: bar.tooltip.split('\n')
      };
    },
    hideTip() {
      this.tip.visible = false;
    }
  }
};
</script>

<style src="@/assets/style.css"></style>
